'use client'

import { faAngleDown, faClose } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  Content,
  Item,
  Link,
  List,
  NavigationMenuViewport,
  Root,
  Trigger,
} from '@radix-ui/react-navigation-menu'
import { useTranslations } from 'next-intl'
import NextLink from 'next/link'
import { type ReactNode } from 'react'
import { cn } from 'utils/cn'
import { usePathActive } from './hooks'
import { Indicator } from './Indicator'
import { getDataByLocale } from './utils'

export const MegaMenu = ({ locale }: { locale: string }) => {
  const t = useTranslations('Navigation.MegaMenu')

  return (
    <Root
      aria-label={t('ariaLabel')}
      className={cn('font-manrope text-body-md-bold relative')}
    >
      <List>
        <Item>
          <MegaMenuTrigger />
          <MegaMenuContent locale={locale} />
        </Item>
      </List>
      <Indicator />
      <div className={cn('absolute right-0 top-full flex justify-center')}>
        <NavigationMenuViewport className='bg-surface-primary @container/viewport data-[state=open]:animate-scaleIn data-[state=closed]:animate-scaleOut shadow-menu relative mt-[10px] h-[var(--radix-navigation-menu-viewport-height)] w-[var(--radix-navigation-menu-viewport-width)] min-w-min origin-[top_right] overflow-x-hidden rounded-lg transition-[width,height] duration-300' />
      </div>
    </Root>
  )
}

export const MegaMenuTrigger = () => {
  const t = useTranslations('Navigation.MegaMenu.MegaMenuTrigger')
  return (
    <Trigger
      className={cn(
        `border-text-400 data-[state=open]:border-primary-700 data-[state=open]:text-primary-700 hover:border-primary-700 hover:text-primary-700 active:border-primary-800 active:text-primary-800 group flex items-center gap-2 rounded-lg border px-5 py-3`,
      )}
      onPointerDown={(e) => {
        e.preventDefault()
        e.stopPropagation()
      }}
    >
      <span className='group-data-[state=closed]:hidden'>{t('open')}</span>
      <span className='group-data-[state=open]:hidden'>{t('closed')}</span>
      <FontAwesomeIcon
        className='block group-data-[state=open]:hidden'
        icon={faAngleDown}
      />
      <FontAwesomeIcon
        className='block group-data-[state=closed]:hidden'
        icon={faClose}
      />
    </Trigger>
  )
}

export const MegaMenuContent = ({ locale }: { locale: string }) => {
  const data = getDataByLocale(locale)

  return (
    <Content
      className={cn(
        `text-text-800 font-manrope text-body-md flex w-[calc(100vw-theme(spacing.20))] min-w-min max-w-[calc(theme(screens.content)-theme(spacing.8))] flex-col gap-4 rounded-lg p-10 pb-0 lg:w-[calc(100vw-theme(spacing.28))]`,
      )}
    >
      <ul className='flex justify-between gap-6'>
        {data.category.map((section, i) => (
          <MenuSection key={i} title={section.title}>
            {section.children.map(({ path, title }, j) => (
              <MenuItem key={j} path={path} title={title} />
            ))}
          </MenuSection>
        ))}
      </ul>
      <div className='text-body-xs self-end pb-4'>Inspires to book</div>
    </Content>
  )
}
const MenuSection = ({
  children,
  title,
}: {
  children: ReactNode
  title: string
}) => (
  <li className='flex flex-col gap-6'>
    <div className='text-body-lg-bold'>{title}</div>
    <ul className='flex flex-col gap-2'>{children}</ul>
  </li>
)

const MenuItem = ({ path, title }: { path: string; title: string }) => {
  const active = usePathActive(path)

  return (
    <li>
      <Link
        asChild
        className={cn(
          `hover:bg-surface-tealgrey-50 focus:bg-surface-tealgrey-50 active:bg-surface-tealgrey-200 -mx-3 block rounded-lg px-3 py-2`,
          {
            'text-primary-700 font-bold': active,
          },
        )}
      >
        <NextLink href={path} prefetch={false}>
          {title}
        </NextLink>
      </Link>
    </li>
  )
}
